// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Components
import { AlertType } from '../../../../../../layout/elements/Alert';
import { Col } from '../../../../../../layout/elements/Col';
import { Drawer, DrawerSize } from '../../../../../../layout/elements/Drawer';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { AlertLine } from '../../../../../../layout/icons';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  getOperationalAlert,
  permissiveStatusToAlarmIndicatorStatus,
  permissiveStatusToIndicatorStatus,
  runningStatusToIndicatorStatus,
} from '../../../../../../plugins/crusher';

// View
import { FeederInterlockProps } from './FeederInterlock.types';
import {
  ColumnBorderRight,
  CrusherContainer,
  CrusherPermissives,
  CrusherStartListDescription,
  FeederInterlockContainer,
  FeederStartContainer,
  GeneralContainer,
  GeneralPermissives,
  HydraulicsContainer,
  HydraulicsPermissives,
  LubricationContainer,
  LubricationPermissives,
} from './FeederInterlock.style';

function FeederInterlock({
  className,
  isDrawerOpen,
  onCloseDrawer,
  unitData,
  edgeStatus,
  testId,
}: FeederInterlockProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();

  const alertText: string | null = getOperationalAlert(
    edgeStatus?.edgeStatus,
    unitData?.timestamp,
    unitData?.readStatus,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  const renderPermissiveItem = (
    permissiveName: string,
    permissiveValue: boolean | null | undefined,
  ): JSX.Element => (
    <Col>
      <CrusherStartListDescription
        status={
          permissiveStatusToIndicatorStatus(
            permissiveValue,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )
        }
      >
        <StatusIndicator
          className="list-text-description"
          text={t(`view.crusher.feederInterlock.drawer.${permissiveName}`)}
          status={
            permissiveStatusToIndicatorStatus(
              permissiveValue,
              edgeStatus ? edgeStatus.edgeStatus : null,
              unitData?.timestamp,
            )
          }
        />
      </CrusherStartListDescription>
    </Col>
  );

  // Inverted Permissive Item (true = alarm, false = good)
  const renderInvertedPermissiveItem = (
    permissiveName: string,
    permissiveValue: boolean | null | undefined,
  ): JSX.Element => (
    <Col>
      <CrusherStartListDescription
        status={
          permissiveStatusToAlarmIndicatorStatus(
            permissiveValue,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )
        }
      >
        <StatusIndicator
          className="list-text-description"
          text={t(`view.crusher.feederInterlock.drawer.${permissiveName}`)}
          status={
            permissiveStatusToAlarmIndicatorStatus(
              permissiveValue,
              edgeStatus ? edgeStatus.edgeStatus : null,
              unitData?.timestamp,
            )
          }
        />
      </CrusherStartListDescription>
    </Col>
  );

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.crusher.feederInterlock.title')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      footer={false}
      alert={
        alertText
          ? {
            type: AlertType.warning,
            icon: <AlertLine />,
            description: alertText,
          }
          : false
      }
      size={DrawerSize.lg}
    >
      <FeederInterlockContainer>
        <Row>
          <FeederStartContainer data-testid={`${testId}-feeder-start-container`}>
            <Col>
              <StatusIndicator
                text={t('view.crusher.feederInterlock.drawer.feederStartPermissive')}
                status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.feederPermissives,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                size={StatusIndicatorSize.lg}
              />
            </Col>
          </FeederStartContainer>
        </Row>

        <Row>
          <ColumnBorderRight xs={24} xl={12} testId={`${testId}-column-border-right`}>
            <div>
              <GeneralContainer data-testid={`${testId}-general-container`}>
                <Col>
                  <Paragraph
                    size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                    weight={ParagraphWeight.bold}
                  >
                    {t('view.crusher.feederInterlock.general')}
                  </Paragraph>
                </Col>
              </GeneralContainer>

              <GeneralPermissives data-testid={`${testId}-general-permissives`}>
                {renderPermissiveItem('eStop', unitData?.eStop)}
                {renderPermissiveItem('beltSlipPermissive', unitData?.beltSlipPermissive)}
                {renderPermissiveItem('feederStarterFault', unitData?.feederStarterFault)}
                {renderInvertedPermissiveItem(
                  'calibrationActive',
                  unitData?.calibrationActive,
                )}

                {/* Discharge Running Status */}
                {unitData?.dischargeSuperiorEnable && (
                  <CrusherStartListDescription>
                    <StatusIndicator
                      text={t('view.crusher.feederInterlock.drawer.dischargeRunningStatus')}
                      status={
                          runningStatusToIndicatorStatus(
                            unitData?.dischargeRunningStatus,
                            edgeStatus ? edgeStatus.edgeStatus : null,
                            unitData?.timestamp,
                          )
                        }
                    />
                  </CrusherStartListDescription>
                )}
              </GeneralPermissives>

              <Col>
                <CrusherContainer data-testid={`${testId}-crusher-container`}>
                  <Paragraph
                    size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                    weight={ParagraphWeight.bold}
                  >
                    {t('view.crusher.feederInterlock.crusher')}
                  </Paragraph>
                </CrusherContainer>

                <CrusherPermissives data-testid={`${testId}-crusher-permissives`}>
                  {renderPermissiveItem(
                    'trampPressureSensorFault',
                    unitData?.trampPressureSensorFault,
                  )}
                  {renderPermissiveItem(
                    'clampPressureSensorFault',
                    unitData?.clampPressureSensorFault,
                  )}
                  {unitData?.eccentricBushingTempEnable && renderPermissiveItem(
                    'highEccentricBushingTemp',
                    unitData?.highEccentricBushingTemp,
                  )}
                  {unitData?.counterShaftBushingTempEnable && renderPermissiveItem(
                    'highCounterShaftBushingTemp',
                    unitData?.highCounterShaftBushingTemp,
                  )}
                  {unitData?.cavityLevelSensorEnable && renderPermissiveItem(
                    'highCavityLevel',
                    unitData?.highCavityLevel,
                  )}
                  {renderPermissiveItem(
                    'highVibrationPermissive',
                    unitData?.highVibrationPermissive,
                  )}
                  {renderPermissiveItem('crusherBowlCreep', unitData?.crusherBowlCreep)}
                  {renderPermissiveItem('minCrusherRunTimer', unitData?.minCrusherRunTimer)}
                  {renderPermissiveItem(
                    'highCrusherCurrentPermissive',
                    unitData?.highCrusherCurrentPermissive,
                  )}
                </CrusherPermissives>

              </Col>
            </div>
          </ColumnBorderRight>

          <Col xs={24} xl={12}>
            <div>
              <HydraulicsContainer data-testid={`${testId}-hydraulics-container`}>
                <Col>
                  <Paragraph
                    size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                    weight={ParagraphWeight.bold}
                  >
                    {t('view.crusher.feederInterlock.hydraulic')}
                  </Paragraph>
                </Col>
              </HydraulicsContainer>

              <HydraulicsPermissives data-testid={`${testId}-hydraulics-permissives`}>
                {renderPermissiveItem(
                  'systemControlPower',
                  unitData?.systemControlPower,
                )}
                {renderPermissiveItem(
                  'crusherOperationMode',
                  unitData?.crusherOperationMode,
                )}
                {renderPermissiveItem('oilLevelLow', unitData?.oilLevelLow)}
                {renderPermissiveItem('hydLowClampPressure', unitData?.hydLowClampPressure)}
                {renderPermissiveItem('hydLowTrampPressure', unitData?.hydLowTrampPressure)}
                {renderPermissiveItem(
                  'hydExcessiveTrampPressure',
                  unitData?.hydExcessiveTrampPressure,
                )}
                {renderPermissiveItem(
                  'hydExcessiveClampPressure',
                  unitData?.hydExcessiveClampPressure,
                )}
                {renderPermissiveItem('highOilTemp', unitData?.highOilTemp)}
                {renderInvertedPermissiveItem('bowlOpenRequest', unitData?.bowlOpenRequest)}
                {renderInvertedPermissiveItem('bowlCloseRequest', unitData?.bowlCloseRequest)}
              </HydraulicsPermissives>
            </div>

            <Col>
              <LubricationContainer data-testid={`${testId}-lubrication-container`}>
                <Paragraph
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  weight={ParagraphWeight.bold}
                >
                  {t('view.crusher.feederInterlock.lubrication')}
                </Paragraph>
              </LubricationContainer>

              <LubricationPermissives data-testid={`${testId}-lubrication-permissives`}>
                {renderInvertedPermissiveItem(
                  'lubePumpStarterFault',
                  unitData?.lubePumpStarterFault,
                )}
                {renderPermissiveItem('lowLubeOilPressure', unitData?.lowLubeOilPressure)}
                {renderPermissiveItem(
                  'lubeDifferentialTempPermissive',
                  unitData?.lubeDifferentialTempPermissive,
                )}
                {renderPermissiveItem(
                  'lubeHighReturnTempPermissive',
                  unitData?.lubeHighReturnTempPermissive,
                )}
              </LubricationPermissives>
            </Col>
          </Col>
        </Row>
      </FeederInterlockContainer>
    </Drawer>
  );
}

export { FeederInterlock };
